import { AfterContentInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/auth';
import { CanvasService, ClientService, RuleViolationService } from '@app/core';
import { FeatureFlagService } from '@app/core/feature-flag.service';
import { Client } from '@app/core/store/client.store';
import { NotificationObject, NotificationType } from '@app/shared/notification/notificationObject';

@Component({
  selector: 'component-top-navigation',
  templateUrl: './top-navigation.component.html',
  styleUrls: ['./top-navigation.component.sass'],
})
export class TopNavigationComponent implements AfterContentInit, OnInit {
  user: any;
  clients: Client[] | undefined;
  selectedClient: Client | undefined;
  menuLinks: { name: string; url: string; adminRequired: boolean }[] = [];

  show = false;
  activatedBy: EventTarget | null;
  ruleViolation = false;

  isAdmin = false;

  notification = new NotificationObject();
  notificationType = NotificationType;

  v3Enabled = true; // v3 is enabled by default
  v4Enabled = false;

  constructor(
    private auth: AuthService,
    private ruleViolationService: RuleViolationService,
    private clientService: ClientService,
    private featureFlagService: FeatureFlagService,
    private canvasService: CanvasService,
    private route: ActivatedRoute,
    public router: Router,
  ) {
    this.ruleViolationService.ruleViolation$.subscribe((violation) => {
      this.ruleViolation = !!violation;
    });

    this.isAdmin = this.auth.getRole() === 'admin';

    this.clientService.getSelectedClient().subscribe((client) => {
      this.selectedClient = client;
      this.updateMenuLinks();
    });
  }

  private updateMenuLinks() {
    this.v4Enabled = this.featureFlagService.isV4EnabledForClient(this.selectedClient, this.isAdmin);
    this.v3Enabled = this.featureFlagService.isV3EnabledForClient(this.selectedClient, this.isAdmin);

    this.menuLinks = [
      ...(this.v3Enabled
        ? [
            {
              name: 'Create',
              url: '/create',
              adminRequired: false,
            },
            {
              name: 'Manage',
              url: '/manage',
              adminRequired: false,
            },
          ]
        : []),
      ...(this.v4Enabled
        ? [
            {
              name: 'Products',
              url: '/products',
              adminRequired: false,
            },
            {
              name: 'Looks',
              url: '/looks',
              adminRequired: false,
            },
          ]
        : []),
      ...(this.isAdmin ? [{ name: 'Widgets', url: '/widgets', adminRequired: false }] : []),
      // Keeping the snapshot tab at the end when visible
      ...(this.v3Enabled ? [{ name: 'Snapshot', url: '/snapshot', adminRequired: true }] : []),
    ];
  }

  ngOnInit() {
    this.clientService.getAllClients().subscribe((clients) => {
      this.clients = clients;
    });
  }

  ngAfterContentInit() {
    this.auth.userProfile$.subscribe((response) => {
      this.user = response;
    });
  }

  logout(): void {
    this.auth.logout();
  }

  showModal(event: Event): void {
    this.activatedBy = event.target;
    this.show = !this.show;
  }

  handleClientChange(event: Event) {
    const client = this.clients?.find((c) => c.uid === (event.target as HTMLSelectElement).value);
    const previousclient = this.selectedClient;
    if (!client || previousclient?.uid === client.uid) {
      return;
    }
    this.clientService.setSelectedClient(client);
    this.canvasService.sendToCanvas(false);
    localStorage.removeItem('lookToEdit');

    // Use the already-initialized isAdmin field
    const v4Enabled = this.featureFlagService.isV4EnabledForClient(client, this.isAdmin);
    const v3Enabled = this.featureFlagService.isV3EnabledForClient(client, this.isAdmin);

    const isCurrentLocationV4 = location.pathname.includes('/products') || location.pathname.includes('/looks');
    const isCurrentLocationV3 = !isCurrentLocationV4;
    // Navigate to the correct root route for the selected client
    if (!v4Enabled && isCurrentLocationV4) {
      // If we are on v4 but the new client does not have v4 enabled, navigate to v3
      this.router.navigate(['/create']).then(() => location.reload());
    } else if (!v3Enabled && isCurrentLocationV3) {
      // If we are on v3 but the new client does not have v3 enabled, navigate to v4
      this.router.navigate(['/products']).then(() => location.reload());
    } else {
      location.reload();
    }
  }

  preventNavigation(): void {
    this.notification.display(this.notificationType.notice, 'Please resolve the rule violation before navigating to other tabs.');
  }

  handleMenuClick(menu: (typeof this.menuLinks)[number]) {
    if (menu.name === 'Widgets') {
      // Force reload of widget page to the root url `/widgets`, in case we were on a subpage
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/widgets']);
      });
    } else if (menu.name === 'Looks') {
      // Force reload of looks page to the root url `/looks`, in case we were on a subpage
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/looks']);
      });
    } else if (menu.name === 'Products') {
      // Force reload of looks page to the root url `/products`, in case we were on a subpage
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/products']);
      });
    }
  }
}
